import {
    AsyncPipe,
    KeyValuePipe,
    NgClass,
    ViewportScroller,
} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLinkActive } from '@angular/router';
import { DeviceService } from '@core/services/device.service';
import { EnvironmentService } from '@core/services/environment.service';
import { LazyAssetComponent } from '@features/lazy-image/components/lazy-asset/lazy-asset.component';
import { NavigationDropdownComponent } from '@features/navigation/components/navigation-dropdown/navigation-dropdown.component';
import {
    TRAVEL_BLOG_MENU,
    TRIPS_MENU,
} from '@features/navigation/navigation-menu-data';
import { FaLogoComponent } from '@shared/fa-logo/fa-logo.component';
import { LinkComponent } from '@shared/link/link.component';
import { UserService } from '../../../user/services/user.service';
import { NavigationSidebarService } from '../../services/navigation-sidebar.service';
@Component({
    selector: 'app-main-navigation',
    templateUrl: './main-navigation.component.html',
    styleUrls: ['./main-navigation.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LinkComponent,
        FaLogoComponent,
        NgClass,
        RouterLinkActive,
        NavigationDropdownComponent,
        LazyAssetComponent,
        AsyncPipe,
        KeyValuePipe,
    ],
})
export class MainNavigationComponent {
    private userService = inject(UserService);
    private environmentService = inject(EnvironmentService);
    private navigationSidebarService = inject(NavigationSidebarService);
    private deviceService = inject(DeviceService);
    private viewport = inject(ViewportScroller);

    navbarInverseColors = input<boolean>(false);

    readonly tripsMenu = TRIPS_MENU;
    readonly travelBlogMenu = TRAVEL_BLOG_MENU;

    isAuthenticated = this.userService.isAuthenticated();
    isSidebarActive = this.navigationSidebarService.isOpen;

    isStaff = this.userService.isStaff();

    isDesktop = toSignal(this.deviceService.isDesktop);

    environment = computed(() => {
        if (this.environmentService.isLocal()) {
            return 'TEST ENVIRONMENT';
        } else if (this.environmentService.isStaging()) {
            return 'STAGING ENVIRONMENT';
        } else if (this.environmentService.isReview()) {
            return 'REVIEW ENVIRONMENT';
        }
        return '';
    });

    showAllTripsDropdown = signal<boolean>(false);
    showTravelBlogDropdown = signal<boolean>(false);

    openDropdown(eventKey: string) {
        if (eventKey === 'trip-dropdown') {
            this.showAllTripsDropdown.set(true);
        } else {
            this.showTravelBlogDropdown.set(true);
        }
    }

    closeDropdown(eventKey: string) {
        if (eventKey === 'trip-dropdown') {
            this.showAllTripsDropdown.set(false);
        } else {
            this.showTravelBlogDropdown.set(false);
        }
    }

    defaultLinkClasses =
        'text-darkgrey h-full overflow-hidden flex gap-0.5 flex-col md:flex-row md:gap-2 justify-center items-center hover:text-red transition duration-200 ease-in-out';

    defautlTripBtnClasses: string[] = [
        'md:gap-2 text-darkgrey h-full overflow-hidden flex flex-col md:flex-row justify-center items-center transition duration-200 ease-in-out',
        'md:py-2 md:border md:rounded-xl md:font-bold',
    ];
    tripsBtnClasses = computed(() => {
        if (this.navbarInverseColors()) {
            return [
                'md:text-darkgrey md:border-lightgrey md:fill-darkgrey md:hover:bg-white',
                ...this.defautlTripBtnClasses,
            ];
        } else {
            return [
                'md:text-white md:border-white hover:bg-[#ffffff4d] md:fill-white',
                ...this.defautlTripBtnClasses,
            ];
        }
    });

    linkClasses = computed(() => {
        if (this.navbarInverseColors()) {
            return ['md:text-black', this.defaultLinkClasses];
        } else {
            return ['md:text-white', this.defaultLinkClasses];
        }
    });

    constructor() {
        effect(() => {
            const isDesktop = this.isDesktop();
            this.viewport.setOffset([0, isDesktop ? 100 : 0]);
        });
    }

    toggleSidebar() {
        this.navigationSidebarService.toggle();
    }

    closeSidebar() {
        this.navigationSidebarService.close();
    }
}
